import { styled } from "@mui/material/styles";
import camelCase from "lodash/camelCase";

import type { HbmlElementNode, HbmlNode } from "@holibob-packages/hbml";

import { HbmlRendererNodes, ReadMoreContainer } from "../../../components";
import { BulletedList } from "../../../components/BulletedList";
import { HbmlElements } from "../../../components/HbmlComponents/HbmlElements";
import { Typography } from "../../../components/Typography";
import { useNextTranslation } from "../../../hooks";
import { ProductContentType } from "../../../types/ProductContent";
import type { ProductContentEntryV2, ProductContentV2 } from "../../types";
import { ProductContentHbmlRenderer } from "../ProductContentHbmlRenderer";
import { ProductExpandButton } from "../ProductExpandButton";
import { ProductNavigationItem } from "../ProductPageNavigation";
import { ProductSectionTitle } from "../ProductSectionTitle";
import { normalizeProductContentToBulletedList } from "../utils/normalizeProductContentToBulletedList";
import { ProductSectionWrapper, ProductSectionWrapperProps } from "./ProductSectionWrapper";

type ProductContentSectionProps = {
    entries: ProductContentEntryV2[];
    type: ProductContentType;
} & Omit<ProductSectionWrapperProps, "children">;

export const ProductContentSection = ({ entries, type, ...props }: ProductContentSectionProps) => {
    const [t] = useNextTranslation("product");

    return (
        <ProductSectionWrapper {...props} data-testid="product-content-section">
            <ProductSectionTitle data-ref="product-content-section-title">
                {t(getTranslationKeyForSection(type))}
            </ProductSectionTitle>
            <ProductContent entries={entries} />
        </ProductSectionWrapper>
    );
};

function getTranslationKeyForSection(section: ProductContentType) {
    return `label.${camelCase(section)}`;
}

type ProductContentProps = {
    entries: ProductContentEntryV2[];
};
const ProductContent = ({ entries }: ProductContentProps) => {
    return (
        <div>
            <ReadMoreContainer ExpandButtonComponent={ProductExpandButton}>
                <ProductContentBulletedList entries={entries} />
            </ReadMoreContainer>
        </div>
    );
};

const COMPONENTS_OVERRIDES = {
    [HbmlElements.BULLETED_LIST]: function BulletedList({ content }: { content: HbmlElementNode }) {
        return (
            <BulletedListStyled>
                <HbmlRendererNodes content={content.children as HbmlNode[]} />
            </BulletedListStyled>
        );
    },
    [HbmlElements.LIST_ITEM]: function ListItemNode({ content }: { content: HbmlElementNode }) {
        return (
            <Typography color="subtle" component="li" size="large">
                <HbmlRendererNodes content={content.children as HbmlNode[]} />
            </Typography>
        );
    },
};

const ProductContentBulletedList = ({ entries }: ProductContentProps) => {
    const content = normalizeProductContentToBulletedList(entries);
    return <ProductContentHbmlRenderer content={content as HbmlNode[]} componentOverrides={COMPONENTS_OVERRIDES} />;
};

const BulletedListStyled = styled(BulletedList)({
    marginTop: "unset",
    marginBottom: "unset",
});

type ProductContentNavigationItem = ProductNavigationItem & { id: ProductContentType };
type UseProductContentSectionsOptions = { showItinerary?: boolean };
export const useProductContentSections = (
    content?: ProductContentV2,
    options?: UseProductContentSectionsOptions
): ProductContentNavigationItem[] => {
    const [t] = useNextTranslation("product");
    const sections: ProductContentNavigationItem[] = [];

    const types: ProductContentType[] = options?.showItinerary
        ? ["ITINERARY", ...CONTENT_TYPE_WHITELIST]
        : CONTENT_TYPE_WHITELIST;

    for (const type of types) {
        const entries = content?.other.get(type);

        sections.push({
            id: type,
            label: t(getTranslationKeyForSection(type)),
            isAvailable: !!entries?.length,
        });
    }

    return sections;
};

const CONTENT_TYPE_WHITELIST: ProductContentType[] = [
    "INCLUSION",
    "EXCLUSION",
    "VENUE",
    "EXHIBITION",
    "OPENING_HOURS",
    "AVAILABLE_DAY",
    "SAFETY_MEASURE",
    "FINISHING_POINT",
    "NOTE",
    "TIMING_NOTE",
    "LOCATION_NOTE",
    "REQUIREMENT",
];
